/**
 * Ленивая загрузка Яндекс.Карт JS API 3.0.
 *
 * Скрипт подключается при первом монтировании карты, а не из index.html: квота
 * бесплатного тарифа считается по загрузкам API, а карта нужна не на каждой странице.
 * Промис кэшируется — сколько бы карт ни было на странице, скрипт грузится один раз.
 */

import { useEffect, useState } from "react";

const SCRIPT_ID = "ymaps3-jsapi";

export const YMAPS_ERROR_NO_KEY = "NO_API_KEY";
export const YMAPS_ERROR_LOAD_FAILED = "SCRIPT_LOAD_FAILED";

let loadPromise = null;

export function getYmapsApiKey() {
  return (process.env.REACT_APP_YANDEX_MAPS_API_KEY || "").trim();
}

/**
 * @returns {Promise<object>} глобальный `ymaps3` после `ymaps3.ready`
 */
export function loadYmaps() {
  const apiKey = getYmapsApiKey();
  // Отсутствие ключа не кэшируем: иначе после правки .env помогал бы только полный перезапуск.
  if (!apiKey) return Promise.reject(new Error(YMAPS_ERROR_NO_KEY));

  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (window.ymaps3) {
      resolve(window.ymaps3);
      return;
    }

    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      // Ключ ограничен по HTTP Referer, а Django отдаёт страницы с
      // Referrer-Policy: same-origin — без явной политики браузер не шлёт
      // Referer кросс-доменно и Яндекс отвечает 403 Invalid api key.
      script.referrerPolicy = "strict-origin-when-cross-origin";
      script.src = `https://api-maps.yandex.ru/v3/?apikey=${encodeURIComponent(apiKey)}&lang=ru_RU`;
      document.head.appendChild(script);
    }

    script.addEventListener("load", () => resolve(window.ymaps3));
    script.addEventListener("error", () => reject(new Error(YMAPS_ERROR_LOAD_FAILED)));
  })
    .then((ymaps3) => ymaps3.ready.then(() => ymaps3))
    .catch((err) => {
      // Даём следующему монтированию шанс повторить загрузку.
      loadPromise = null;
      throw err;
    });

  return loadPromise;
}

/**
 * @returns {{status: 'loading'|'ready'|'error', ymaps3: object|null, error: Error|null}}
 */
export function useYmaps() {
  const [state, setState] = useState({ status: "loading", ymaps3: null, error: null });

  useEffect(() => {
    let cancelled = false;

    loadYmaps()
      .then((ymaps3) => {
        if (!cancelled) setState({ status: "ready", ymaps3, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ status: "error", ymaps3: null, error });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
